import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { VivoService } from '../vivo.service';
import { cloneDeep, groupBy } from 'lodash-es';
import { VivoMapping } from '../vivo-mapping.model';
import { BaseComponent } from '../../components/general/base/base.component';
import { take } from 'rxjs/operators';
import { MapService } from '../../shared/map/map.service';
import { convertFeatureToMapObject } from '../../shared/utils';
import { MapObject } from '../../shared/map/types';
import { TranslateService } from '@ngx-translate/core';
import { ILayer } from '../../shared/map/layer/layer.type';

interface Pagination {
  limit: number;
  total: number;
  page: number;
}

@Component({
  selector: 'qm-vivo-admin',
  templateUrl: './vivo-admin.component.html',
  styleUrls: ['./vivo-admin.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class VivoAdminComponent extends BaseComponent implements OnInit {

  public assetList: any[];

  public editAsset: any;
  public currentMapping: string | null;
  public mapObjectList: MapObject[] = [];
  public mappingList: VivoMapping[] = [];
  public selectedLayer: string;
  public layers: ILayer[];
  public prefix = '';
  public postfix = '';
  public regoId: string;
  public query: string;
  public autocompleteResults: MapObject[] = [];
  public pagination: Pagination = {
    limit: 300,
    total: 0,
    page: 1
  };
  public loading: boolean;

  constructor(private vivoService: VivoService,
    private mapService: MapService,
    private translateService: TranslateService,
    private changeDetectorRef: ChangeDetectorRef) {
    super();
    this.name = 'VivoAdmin';
  }

  ngOnInit() {
    console.log('VIVO admin init');
    this.layers = this.mapService.getLayerList().filter(layer => layer.metadata.vivoLinkable);
    this.loadAssets();
  }

  public doSearch() {
    this.currentMapping = null;
    if (this.query && this.query.length >= 2) {
      this.autocompleteResults = this.mapObjectList.filter(v => {
        if (v.title === null) {
          console.error(v);
          return false;
        }
        return v.title.toLowerCase().indexOf(this.query.toLowerCase()) > -1;
      }).slice(0, 20);
    } else {
      this.autocompleteResults = [];
    }
  }

  public selectMapObject(mapObject: MapObject) {
    this.currentMapping = mapObject.id;
  }

  private async loadAssets() {
    this.loading = true;
    const vivoLinkableLayerIds = this.layers.map(layer => layer.id);
    const list = await this.vivoService.list();
    const assets = await this.vivoService.getAllAssets(this.pagination.limit, this.pagination.page);
    this.mappingList = [];
    if (list.isOk) {
      // Duplicator will automatically remove all duplicate mappings! Buahaha!
      this.duplicator(list.data);
      for (const row of list.data) {
        this.mappingList[row.vivoId] = row;
      }
    }
    if (assets) {
      this.pagination.limit = assets.limit;
      this.pagination.page = assets.page;
      this.pagination.total = assets.total;

      this.assetList = assets.docs;
      this.mapObjectList = Array.from(this.mapService.getLayerFeatures()).flatMap(([layerId, features]) => {
        if (vivoLinkableLayerIds.includes(layerId)) {
          return features.map(feature => convertFeatureToMapObject(feature, this.translateService.instant(feature.layer.name)));
        }
        return [];
      });
      console.log(this.mapObjectList);
      this.loading = false;
      this.changeDetectorRef.markForCheck();
    }
  }

  private duplicator(list: VivoMapping[]) {
    console.log(list);
    const group = groupBy(list, (o) => {
      return o.vivoId;
    });
    console.log(group);
    Object.keys(group).forEach(key => {
      if (group[key].length > 1) {
        // console.log('Duplicates found for');
        console.log(key);
        console.log(group[key]);
        group[key].forEach(mapping => {
          this.vivoService.remove(mapping);
        });
      }
    });
  }

  public setEditAsset(asset: any) {
    this.editAsset = cloneDeep(asset);
    this.currentMapping = null;
    console.log(this.editAsset);
  }

  public async saveAssetMapping() {
    if (this.currentMapping) {
      const m = new VivoMapping();
      if (this.mappingList[this.editAsset._id]) {
        console.log('DUPLICATES! FREEZE!');
        return;
      }
      m.regoId = this.currentMapping;
      m.vivoId = this.editAsset._id;
      m.type = this.getAssetType(this.editAsset);
      this.mappingList[this.editAsset._id] = m;
      await this.vivoService.create(m);
      this.currentMapping = null;
      this.editAsset = {};
      this.changeDetectorRef.markForCheck();
      // this.loadAssets();
    }
  }

  public getTitle(id: string): string {
    const r = this.mapObjectList.find(o => {
      return o.id + '' === id + '';
    });
    return r ? r.title : id;
  }

  public getAssetType(asset: any): any {
    return asset.organization ? 'Organization' : 'Asset';
  }

  public automap() {
    if (!this.selectedLayer) {
      return;
    }
    const featuresForLayer = this.mapObjectList.filter(o => {
      return o.layerId === this.selectedLayer;
    });
    console.log(this.mapObjectList);
    console.log(featuresForLayer);
    for (let i = 0; i < this.assetList.length; i++) {
      if (this.mappingList[this.assetList[i]._id]) {
        continue;
      }
      const name = this.checkPostfix(this.checkPrefix(this.assetList[i].name));
      // console.log('MAP', name);
      let regoObject = featuresForLayer.find(o => {
        return o.title === name || o.id === name;
      });
      if (regoObject) {
        const m = new VivoMapping();
        m.regoId = regoObject.id;
        m.vivoId = this.assetList[i]._id;
        m.type = this.getAssetType(this.assetList[i]);
        this.mappingList[this.assetList[i]._id] = m;
        this.vivoService.create(m);
      } else {
        regoObject = featuresForLayer.find(o => {
          return o.title.includes(name) || (typeof o.id === 'string' && o.id.includes(name));
        });
        if (regoObject) {
          const m = new VivoMapping();
          m.regoId = regoObject.id;
          m.vivoId = this.assetList[i]._id;
          m.type = this.getAssetType(this.assetList[i]);
          this.mappingList[this.assetList[i]._id] = m;
          this.vivoService.create(m);
        }
      }
    }
    console.log(this.mappingList);
  }

  private checkPrefix(name: string): string {
    if (this.prefix.startsWith('!')) {
      if (name.startsWith(this.prefix.substring(1))) {
        return name.substring(this.prefix.length - 1);
      } else {
        return name;
      }
    } else {
      return this.prefix + name;
    }
  }

  private checkPostfix(name: string): string {
    if (this.postfix.startsWith('!')) {
      if (name.endsWith(this.postfix.substring(1))) {
        return name.substring(0, name.length - (this.postfix.length - 1));
      } else {
        return name;
      }
    } else {
      return name + this.postfix;
    }
  }

  public assetVisible(asset: any): boolean {
    return true;
  }

  public getRowClass(asset: any): string {
    return '';
  }

  public async UnMapDevice() {
    const mapping = this.mappingList[this.editAsset._id];
    await this.vivoService.remove(mapping);
    this.mappingList[this.editAsset._id] = null;
    this.editAsset = {};
    // this.loadAssets();
  }

  public turnPage(page: any) {
    console.log('turn page', event);
    if (page !== this.pagination.page) {
      this.pagination.page = page;
    }
    this.loadAssets();
  }

  public getAssetName(asset: any) {
    return asset.holder?.name ? `${asset.name}, ${asset.holder.name}` : asset.name;
  }
}
